import {
  Alert,
  Button,
  ButtonVariant,
  DatePicker,
  DescriptionList,
  DescriptionListDescription,
  DescriptionListGroup,
  DescriptionListTerm,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  ModalVariant,
  Split,
  SplitItem,
  TimePicker,
} from '@patternfly/react-core';
import {useEffect, useState} from 'react';
import {AlertVariant, useUI} from 'src/contexts/UIContext';
import {useSetExpiration} from 'src/hooks/UseTags';
import {getErrorMessageFromUnknown} from 'src/resources/ErrorHandling';
import {formatDate, isNullOrUndefined} from 'src/libs/utils';
import {is24HourFormat} from 'src/libs/dateTimeUtils';

export default function EditExpirationModal(props: EditExpirationModalProps) {
  const [date, setDate] = useState<Date>(null);
  const {addAlert} = useUI();
  const {
    setExpiration,
    successSetExpiration,
    errorSetExpiration,
    errorSetExpirationDetails,
  } = useSetExpiration(props.org, props.repo);
  const [validDate, setValidDate] = useState<boolean>(true);
  const initialDate: Date = isNullOrUndefined(props.expiration)
    ? null
    : new Date(props.expiration);

  // Compute mutable tags by filtering out immutable ones
  const mutableTags = props.tags.filter(
    (tag) => !props.immutableTags?.includes(tag),
  );
  const allTagsImmutable = mutableTags.length === 0 && props.tags.length > 0;

  const isToday = (date: Date) => {
    const today = new Date();
    return (
      date.getFullYear() == today.getFullYear() &&
      date.getMonth() == today.getMonth() &&
      date.getDate() == today.getDate()
    );
  };

  useEffect(() => {
    setDate(initialDate);
  }, [props.expiration]);

  useEffect(() => {
    if (successSetExpiration) {
      const dateMessage: string = isNullOrUndefined(date)
        ? 'never'
        : formatDate(date.getTime() / 1000);
      const title: string =
        mutableTags.length === 1
          ? `Successfully set expiration for tag ${mutableTags[0]} to ${dateMessage}`
          : `Successfully updated tag expirations to ${dateMessage}`;
      addAlert({variant: AlertVariant.Success, title: title});
      props.loadTags();
      props.setIsOpen(false);
      if (!isNullOrUndefined(props.onComplete)) {
        props.onComplete();
      }
    }
  }, [successSetExpiration]);

  useEffect(() => {
    if (errorSetExpiration) {
      const title: string =
        mutableTags.length === 1
          ? `Could not set expiration for tag ${mutableTags[0]}`
          : 'Could not update tag expirations';
      const errorDisplayMessage = (
        <>
          {Array.from(errorSetExpirationDetails.getErrors()).map(
            ([tag, error]) => (
              <p key={tag}>
                Could not update expiration for tag {tag}:{' '}
                {getErrorMessageFromUnknown(error.error)}
              </p>
            ),
          )}
        </>
      );
      addAlert({
        variant: AlertVariant.Failure,
        title: title,
        message: errorDisplayMessage,
      });
      props.setIsOpen(false);
      if (!isNullOrUndefined(props.onComplete)) {
        props.onComplete();
      }
    }
  }, [errorSetExpiration]);

  const dateFormat = (date: Date) => {
    if (!isNullOrUndefined(date)) {
      return date.toLocaleDateString(navigator.language, {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    }
  };

  const onDateChange = (
    _event: React.FormEvent<HTMLInputElement>,
    _value: string,
    dateValue?: Date,
  ) => {
    const isInputInvalid = dateValue !== null && dateValue === undefined;
    if (!isInputInvalid) {
      const newDate = isNullOrUndefined(date) ? new Date() : new Date(date);
      // Set day to 1 first to avoid JavaScript date rollover when changing months.
      // Example: If newDate is Jan 29 and we set month to February, JavaScript would
      // roll over to March 1 because Feb doesn't have day 29. Setting day to 1 first
      // prevents this.
      newDate.setDate(1);
      newDate.setFullYear(dateValue.getFullYear());
      newDate.setMonth(dateValue.getMonth());
      newDate.setDate(dateValue.getDate());
      if (isNullOrUndefined(date) && isToday(newDate)) {
        newDate.setHours(newDate.getHours() + 1);
      } else if (isNullOrUndefined(date)) {
        newDate.setHours(0, 0, 0, 0);
      }
      setDate(newDate);
      if (newDate <= new Date()) {
        setValidDate(false);
      } else {
        setValidDate(true);
      }
    } else {
      setValidDate(false);
    }
  };

  const onTimeChange = (
    _event: React.FormEvent<HTMLInputElement>,
    _time: string,
    hour?: number,
    minute?: number,
    _seconds?: number,
    isValid?: boolean,
  ) => {
    const isInputValid = hour !== null && minute !== null && isValid;
    if (isInputValid) {
      const newDate = isNullOrUndefined(date) ? new Date() : new Date(date);
      newDate.setHours(hour, minute);
      if (newDate > new Date()) {
        setValidDate(true);
        setDate(newDate);
      } else {
        setValidDate(false);
      }
    } else {
      setValidDate(false);
    }
  };

  const rangeValidator = (date: Date) => {
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    return date < now ? 'Cannot set expiration date to the past.' : '';
  };

  const onSave = () => {
    const requestedDate =
      date === null || date === undefined ? null : date.getTime() / 1000;
    setExpiration({tags: mutableTags, expiration: requestedDate});
  };

  const onClose = () => {
    props.setIsOpen(false);
    setDate(initialDate);
  };

  const onClear = () => {
    setDate(null);
    setValidDate(true);
  };

  const minExpirationDateTime = () => {
    if (date !== null && isToday(date)) {
      return new Date(); // now
    } else {
      const newDate = new Date();
      newDate.setHours(0, 0, 0);
      return newDate;
    }
  };

  return (
    <>
      <Modal
        id="edit-expiration-modal"
        isOpen={props.isOpen}
        onClose={onClose}
        variant={ModalVariant.medium}
        style={{
          overflowX: 'visible',
          overflowY: 'visible',
        }}
      >
        <ModalHeader title="Change Tags Expiration" />
        <ModalBody>
          {allTagsImmutable ? (
            <Alert
              isInline
              variant="danger"
              title="All selected tags are immutable"
              data-testid="all-tags-immutable-error"
            >
              None of the selected tags can have their expiration changed
              because they are all immutable.
            </Alert>
          ) : (
            props.immutableTags &&
            props.immutableTags.length > 0 && (
              <>
                <Alert
                  isInline
                  variant="warning"
                  title="Immutable tags will be skipped"
                  data-testid="immutable-tags-expiration-warning"
                >
                  The following tags are immutable and will not have their
                  expiration changed: {props.immutableTags.join(', ')}
                </Alert>
                <div style={{marginBottom: '1rem'}} />
              </>
            )
          )}
          <DescriptionList>
            <DescriptionListGroup>
              <DescriptionListTerm>
                Tags that will be updated
              </DescriptionListTerm>
              <DescriptionListDescription id="edit-expiration-tags">
                {mutableTags.map((tag) => (
                  <Label key={tag}>{tag}</Label>
                ))}
              </DescriptionListDescription>
            </DescriptionListGroup>
            <DescriptionListGroup>
              <DescriptionListTerm>Expiration date</DescriptionListTerm>
              <DescriptionListDescription>
                <Split hasGutter style={{height: '4em'}}>
                  <SplitItem>
                    <DatePicker
                      placeholder="No date selected"
                      value={dateFormat(date)}
                      dateFormat={dateFormat}
                      dateParse={(date: string) => new Date(date)}
                      onChange={onDateChange}
                      validators={[rangeValidator]}
                      appendTo={() =>
                        document.getElementById(
                          'edit-expiration-modal',
                        ) as HTMLElement
                      }
                    />
                  </SplitItem>
                  <SplitItem>
                    <TimePicker
                      id="expiration-time-picker"
                      placeholder="No time selected"
                      time={
                        date === null || !validDate
                          ? ' '
                          : date.toLocaleTimeString()
                      }
                      onChange={onTimeChange}
                      is24Hour={is24HourFormat()}
                      minTime={minExpirationDateTime()}
                      invalidMinMaxErrorMessage="Cannot set expiration date to the past."
                      style={{width: '150px', whiteSpace: 'normal'}}
                    />
                  </SplitItem>
                  <SplitItem>
                    <Button variant={ButtonVariant.secondary} onClick={onClear}>
                      Clear
                    </Button>
                  </SplitItem>
                </Split>
              </DescriptionListDescription>
            </DescriptionListGroup>
          </DescriptionList>
        </ModalBody>
        <ModalFooter>
          <Button key="cancel" variant="primary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            key="modal-action-button"
            variant="primary"
            isDisabled={!validDate || allTagsImmutable}
            onClick={onSave}
          >
            Change Expiration
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
}

interface EditExpirationModalProps {
  org: string;
  repo: string;
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  tags: string[];
  loadTags: () => void;
  expiration?: string;
  onComplete?: () => void;
  immutableTags?: string[];
}
